/**
 * usePartnerOfferGate — does this paywall open show the partner offer sheet?
 * (partner codes build item 5 / C5, 2026-09-27; utils/partnerOfferSheet.ts has the rules.)
 *
 * PaywallModal calls it on every open. Every native paywall MOUNTS FRESH when it opens (the
 * Modal renders nothing while hidden), so its own status is still loading on the first render
 * — the review of #4115 (H1) found the first version deciding right there, with
 * `partner_offer` still null, so the sheet never showed. The decision is therefore taken on
 * the first render where the status is KNOWN, and latched for the rest of the open (a refresh
 * mid-open never swaps one purchase screen for the other):
 *
 *   - off a phone: `none` at once, always (the web sells nothing);
 *   - the status could not be read at all (`statusUnavailable`): `none` at once;
 *   - the status still loading: `checking` — PaywallModal shows a neutral spinner, never the
 *     wrong paywall and never a blank screen;
 *   - known, and not a discount holder whose purchase is live (`shouldCheckPartnerOffer` — for
 *     everyone while the program or its purchase kill switch is off): `none`, nothing fetched;
 *   - known and a candidate: `checking` while the server is asked, then `show` for an eligible
 *     offer with a plan for this store, else `none`.
 *
 * ONE budget per open: whatever is still being waited for PARTNER_GATE_TIMEOUT_MS after the
 * open (the status, then the server) is `none`, and a late answer never swaps it. `fallBack()`
 * turns the sheet off for the rest of the open ("See all plans", nothing sellable).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { fetchPartnerPurchaseOffer } from '@/services/partnerOfferApi';
import {
  PARTNER_GATE_TIMEOUT_MS,
  gateAnswerFor,
  isPhone,
  partnerGateStatus,
  shouldCheckPartnerOffer,
  type PartnerGateAnswer,
  type PartnerGateStatus,
  type PartnerOfferSummary,
  type PartnerPurchaseOffer,
} from '@/utils/partnerOfferSheet';

export interface PartnerOfferGate {
  status: PartnerGateStatus;
  offer: PartnerPurchaseOffer | null;
  fallBack: () => void;
}

const NONE: PartnerGateAnswer = { status: 'none', offer: null };

export function usePartnerOfferGate(input: {
  visible: boolean;
  /** This paywall's own subscription status has loaded (useSubscription `!isLoading`). */
  statusKnown: boolean;
  /** The status could not be read and nothing was saved to show (useSubscription). */
  statusUnavailable: boolean;
  partnerOffer: PartnerOfferSummary | null | undefined;
  cedarCohort: string | null | undefined;
  timeBoundTrial?: boolean;
}): PartnerOfferGate {
  const { visible, statusKnown, statusUnavailable, partnerOffer, cedarCohort, timeBoundTrial } = input;
  const phone = isPhone(Platform.OS);

  // Per open: when it opened, and the decision — null until the status is known, then latched.
  const latch = useRef<{ open: boolean; openedAt: number; decided: boolean | null }>(
    { open: false, openedAt: 0, decided: null },
  );
  if (visible && !latch.current.open) {
    latch.current = { open: true, openedAt: Date.now(), decided: null };
  } else if (!visible && latch.current.open) {
    latch.current = { open: false, openedAt: 0, decided: null };
  }
  if (phone && visible && latch.current.decided === null) {
    if (statusUnavailable) {
      latch.current.decided = false;
    } else if (statusKnown) {
      latch.current.decided = shouldCheckPartnerOffer({ platform: Platform.OS, partnerOffer, cedarCohort, timeBoundTrial });
    }
  }
  const decided = latch.current.decided;
  const [answer, setAnswer] = useState<PartnerGateAnswer | null>(null);

  // A closed paywall forgets its answer: the next open asks again.
  useEffect(() => { if (!visible) setAnswer(null); }, [visible]);

  // The one budget: still waiting at openedAt + PARTNER_GATE_TIMEOUT_MS → none.
  useEffect(() => {
    if (!phone || !visible || decided === false) return;
    const left = Math.max(0, latch.current.openedAt + PARTNER_GATE_TIMEOUT_MS - Date.now());
    const timer = setTimeout(() => setAnswer((a) => a ?? NONE), left);
    return () => clearTimeout(timer);
  }, [phone, visible, decided]);

  // A candidate: ask the server once; the first answer (or the budget) wins.
  useEffect(() => {
    if (!phone || !visible || decided !== true || answer) return;
    let alive = true;
    fetchPartnerPurchaseOffer(Platform.OS)
      .then((offer) => { if (alive) setAnswer((a) => a ?? gateAnswerFor(offer)); })
      .catch(() => { if (alive) setAnswer((a) => a ?? NONE); });
    return () => { alive = false; };
  }, [phone, visible, decided, answer]);

  const fallBack = useCallback(() => setAnswer(NONE), []);
  const status = partnerGateStatus({ phone, visible, decided, answer });
  return { status, offer: status === 'show' ? answer?.offer ?? null : null, fallBack };
}
